import { post } from '@/infra/rest';
import { PostUserMessageRequest, PostUserMessageResponse } from '@/modules/vendors/types';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CreateTPEChatSessionRequest,
  CreateTPEChatSessionResponse,
  GetTPEChatHistoryRequest,
  GetTPEChatHistoryResponse,
  SendTPEMessageRequest,
  SendTPEMessageResponse
} from '../types';

// Mock API base URL - replace with actual endpoint
const TPE_CHAT_API_BASE = '/api/v1/tpe-chat';

// API functions (replace these with actual API calls to your backend)
const createTPEChatSession = async (
  data: CreateTPEChatSessionRequest
): Promise<CreateTPEChatSessionResponse> => {
  // Mock implementation - replace with actual API call
  const response = await fetch(`${TPE_CHAT_API_BASE}/sessions`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify(data),
  });
  
  if (!response.ok) {
    throw new Error('Failed to create chat session');
  }
  
  return response.json();
};

const sendTPEMessage = async (
  data: SendTPEMessageRequest
): Promise<SendTPEMessageResponse> => {
  // Create the message payload with chat_id at top level and message nested
  const requestPayload: PostUserMessageRequest = {
    chat_id: data.session_id,
    message: {
      id: `msg_${Date.now()}`, // FIXME: Generate proper UUID
      role: 'user' as const,
      type: 'text' as const,
      created_at: new Date().toISOString(),
      text: data.message,
      attachments: data.attachments || [] // Pass attachments from request data
    }
  };

  const response = await post<PostUserMessageRequest, PostUserMessageResponse>({
    url: '/v0/tpra/chat/message',
    data: requestPayload,
    config: {
      headers: {
        token: data.token,
      },
    },
  });

  // Transform the response to match SendTPEMessageResponse
  return {
    success: response.success,
    message_id: response.data.id,
    response_message: undefined, // No response message in this structure
    error: response.success ? undefined : response.message
  };
};

const getTPEChatHistory = async (
  data: GetTPEChatHistoryRequest
): Promise<GetTPEChatHistoryResponse> => {
  // Mock implementation - replace with actual API call
  const response = await fetch(`${TPE_CHAT_API_BASE}/sessions/${data.session_id}/messages`);
  
  if (!response.ok) {
    throw new Error('Failed to fetch chat history');
  }
  
  return response.json();
};

// React Query hooks
export const useCreateTPEChatSession = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: createTPEChatSession,
    onSuccess: (data) => {
      // Invalidate and refetch any related queries
      queryClient.invalidateQueries({ queryKey: ['tpe-chat-sessions'] });
    },
  });
};

export const useSendTPEMessage = () => {
  const queryClient = useQueryClient();
  
  return useMutation({
    mutationFn: sendTPEMessage,
    onSuccess: (data) => {
      // Invalidate chat history queries
      queryClient.invalidateQueries({ queryKey: ['tpe-chat-history'] });
    },
  });
};

export const useGetTPEChatHistory = (sessionId: string | null, enabled = true) => {
  return useQuery({
    queryKey: ['tpe-chat-history', sessionId],
    queryFn: () => getTPEChatHistory({ session_id: sessionId! }),
    enabled: enabled && !!sessionId,
    refetchOnWindowFocus: false,
    staleTime: 30000, // Consider data fresh for 30 seconds
  });
};